import { ChevronDownIcon } from 'lucide-react';

import { ItemContent } from '@/components/item/itemContent';
import { useCollapsibleState } from '@/components/table/collapsibleCell';
import { CollapsibleRow } from '@/components/table/collapsibleRow';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type { Gift } from '@/models/gift';

import { GiftSchedule } from './GiftSchedule';

interface GiftsTableProps {
  gifts: Gift[];
  /** Forwarded to `GiftSchedule`: the timeline as it will be once published. */
  published?: boolean;
}

/** Gifts by code and schedule, each opening on what it grants. */
export const GiftsTable = ({ gifts, published }: GiftsTableProps) => {
  const collapsible = useCollapsibleState(gifts.map(({ gift_id }) => gift_id));

  return (
    <Table noWrapper>
      <TableHeader className="bg-transparent">
        <TableRow>
          <TableHead className="w-8">
            <Button
              variant="ghost"
              size="icon"
              className="size-4"
              aria-label={
                collapsible.isAllExpanded ? 'Collapse all' : 'Expand all'
              }
              onClick={collapsible.toggleAll}
            >
              <ChevronDownIcon
                className={cn(
                  'size-4 transition-transform duration-200',
                  collapsible.isAllExpanded && 'rotate-180',
                )}
              />
            </Button>
          </TableHead>
          <TableHead>Gift Code</TableHead>
          <TableHead>Schedule</TableHead>
        </TableRow>
      </TableHeader>
      {gifts.map((gift) => (
        <CollapsibleRow
          key={gift.gift_id}
          open={collapsible.isExpanded(gift.gift_id)}
          onToggle={() => {
            collapsible.toggle(gift.gift_id);
          }}
          expanded={
            <ItemContent itemId={gift.item_id} quantity={gift.item_quantity} />
          }
          colSpan={3}
        >
          <TableCell>
            <div className="font-mono">{gift.gift_code}</div>
            <Text variant="caption">{gift.name}</Text>
          </TableCell>
          <TableCell className="whitespace-normal">
            <GiftSchedule gift={gift} published={published} />
          </TableCell>
        </CollapsibleRow>
      ))}
    </Table>
  );
};
